// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Close from "@store/Library/Components/Toolkit/Icons/Close";
import getReactNodeText from "@store/Framework/Common/Utilities/getReactNodeText";
import hashString from "@store/Framework/Common/Utilities/hashString";
import { useIsMaintenanceBannerEnabled } from "@store/Framework/Store/Context/ContextHooks";
import SettingsLine from "@store/Library/Components/Toolkit/Icons/Settings/SettingsLine";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { focusStyles, siteWidthStyles } from "@toolkit/Themes/common/mixins";
import React, { FC, useState } from "react";
import { css } from "styled-components";

const closedCookieName = "maintenanceBannerClosed";

export interface MaintenanceBannerPresentationProps extends ExpandStyles<MaintenanceBannerStyles> {}

export interface MaintenanceBannerProps extends MaintenanceBannerPresentationProps {}

export interface MaintenanceBannerStyles {
    wrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    icon?: IconPresentationProps;
    text?: TypographyPresentationProps;
    closeClickable?: ClickablePresentationProps;
    closeIcon?: IconPresentationProps;
}

export const maintenanceBannerStyles: MaintenanceBannerStyles = {
    wrapper: {
        css: css`
            width: 100%;
            background-color: ${getColor("warning")};
            color: ${getColor("warning.contrast")};

            @media print {
                display: none;
            }
        `,
    },
    contentWrapper: {
        css: css`
            ${siteWidthStyles()}
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: ${pxToRem(4)};
            padding-top: ${pxToRem(11)};
            padding-bottom: ${pxToRem(11)};
        `,
    },
    icon: {
        color: "inherit",
        size: 20,
        src: SettingsLine,
        css: css`
            flex-shrink: 0;
            align-self: flex-start;
        `,
    },
    text: {
        forwardAs: "div",
        color: "inherit",
        size: 14,
        css: css`
            padding-right: ${pxToRem(24)};
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    closeClickable: {
        css: css`
            ${focusStyles()}
            position: absolute;
            top: 50%;
            display: flex;
            align-items: center;
            transform: translateY(-50%);
            ${x => breakpointValues(x.theme.design.layout.spacing.side, "right")}
        `,
    },
    closeIcon: {
        color: "inherit",
        size: 16,
        src: Close,
    },
};

/**
 * The announcement bar shown at the top of every page while the Maintenance Banner setting is on.
 */
const MaintenanceBanner: FC<MaintenanceBannerProps> = ({ extendedStyles, overridedStyles }) => {
    const styles = useExpandProps(maintenanceBannerStyles, extendedStyles, overridedStyles);
    const isEnabled = useIsMaintenanceBannerEnabled();
    const message = siteMessage("MaintenanceBanner_Content");
    // The session cookie holds a hash of the closed message, so a changed announcement is shown again.
    const messageHash = hashString(getReactNodeText(message).trim());
    const [closedHash, setClosedHash] = useState(() => getCookie(closedCookieName));

    if (!isEnabled || closedHash === messageHash) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} role="status" data-test-selector="maintenanceBanner">
            <StyledWrapper {...styles.contentWrapper}>
                <Icon {...styles.icon} />
                <Typography {...styles.text} data-test-selector="maintenanceBanner_Content">
                    {message}
                </Typography>
                <Clickable
                    {...styles.closeClickable}
                    data-test-selector="maintenanceBanner_Close"
                    onClick={() => {
                        setCookie(closedCookieName, messageHash);
                        setClosedHash(messageHash);
                    }}
                >
                    <Icon {...styles.closeIcon} />
                    <VisuallyHidden>{translate("Close")}</VisuallyHidden>
                </Clickable>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default MaintenanceBanner;
